import { useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, PackageCheck, X } from "lucide-react";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampSelection, ChampTexte } from "@/composants/ui/champ";
import { Chargement, Erreur } from "@/composants/ui/etats";
import { messageDe, useChargement } from "@/lib/chargement";
import { MOTIFS_NON_CONFORMITE, quantiteLisible } from "@/lib/libelles";
import {
  api,
  type CommandeAReceptionner,
  type FicheCommandeFournisseur,
  type LigneDeLaFicheFournisseur,
  type MotifNonConformite,
  type SaisieBl,
} from "@/lib/api";

export type SaisieLigne = {
  recue: string;
  nonConforme: string;
  motif: string;
  commentaire: string;
  /** Ticket #24 : ce que le BL annonce, et le manquant confirmé par la logistique. */
  annoncee: string;
  manquantConfirme: boolean;
};

const VIDE: SaisieLigne = {
  recue: "",
  nonConforme: "",
  motif: "",
  commentaire: "",
  annoncee: "",
  manquantConfirme: false,
};

const OPTIONS_MOTIF = [
  { valeur: "", libelle: "Choisir un motif" },
  ...Object.entries(MOTIFS_NON_CONFORMITE).map(([valeur, libelle]) => ({ valeur, libelle })),
];

const nombre = (texte: string) => Number(texte.replace(",", "."));

/**
 * Le manquant que l'écran propose (ticket #24) : ce que le BL annonce et qui
 * n'est pas arrivé. L'écran l'affiche ; la base refuse de le confirmer là où rien
 * ne manque, et l'ouvre.
 */
const manquant = (saisie: SaisieLigne) => {
  const annoncee = saisie.annoncee.trim() ? nombre(saisie.annoncee) : null;
  const recue = nombre(saisie.recue) || 0;
  return annoncee !== null && recue < annoncee ? annoncee - recue : null;
};

/** Une réception constatée, où joindre les photos de sa commande. */
type Constatee = { id: string; commande_fournisseur_id: string };

/**
 * Une photo choisie, et ce qu'elle montre (décision 74) : les lignes de la commande
 * qu'elle désigne, aucune si elle montre la réception entière.
 */
type PhotoChoisie = { fichier: File; lignes: string[] };

/**
 * Les lignes sur lesquelles on constate : celles où l'on agit, qui attendent encore
 * quelque chose. Une ligne annulée entière n'attend plus rien.
 */
const lignesAReceptionner = (fiche: FicheCommandeFournisseur) =>
  fiche.lignes.filter(
    (ligne) => ligne.intervenable && ligne.quantite_annulee < ligne.quantite_commandee,
  );

/**
 * Constater une réception à la main (LOT 2, ticket #22, ADR-0046) : par ligne, ce
 * qui est arrivé physiquement — même au-delà du commandé, LABGEST ne bloque pas
 * la réalité — et, parmi ce qui est arrivé, ce qui n'est pas conforme, son motif
 * et un commentaire ; des photos, le numéro et le fichier du BL.
 *
 * Un BL commun (décision 31) couvre plusieurs commandes d'un même fournisseur :
 * on les ajoute ici, et LABGEST crée une réception par commande, reliées au même
 * BL, et chaque commande garde son commentaire. Une ligne laissée vide n'a rien
 * reçu : l'écran ne l'envoie pas. Le reste est la base : elle refuse un non
 * conforme supérieur au reçu, ouvre les problèmes et dérive l'avancement.
 */
export function PageReceptionNouvelle() {
  const [parametres, setParametres] = useSearchParams();
  const identifiants = (parametres.get("commandes") ?? "").split(",").filter(Boolean);
  const [fichierBl, setFichierBl] = useState<File | null>(null);

  if (identifiants.length === 0) {
    return <Erreur message="Aucune commande fournisseur à réceptionner n'est désignée." />;
  }
  return (
    <FormulaireReception
      identifiants={identifiants}
      changerCommandes={(ids) => setParametres({ commandes: ids.join(",") })}
      fichierBl={
        <Champ
          libelle="Fichier du BL"
          type="file"
          accept="application/pdf,image/*"
          className="py-2"
          onChange={(evenement) => setFichierBl(evenement.target.files?.[0] ?? null)}
        />
      }
      envoyer={async (saisie) =>
        api.constaterReception(
          {
            numero_bl: saisie.numero_bl,
            parts: saisie.parts.map((part) => ({
              ...part,
              lignes: part.lignes.map(
                ({ quantite_annoncee: _a, manquant_confirme: _m, ...ligne }) => ligne,
              ),
            })),
          },
          fichierBl,
        )
      }
    />
  );
}

/**
 * Le formulaire d'une réception, à la main ou proposé par un BL lu (ticket #24).
 * Depuis un BL, chaque ligne dit ce que le BL annonce — la lecture le propose,
 * la logistique le corrige ; si le reçu saisi est moindre, l'écran propose le
 * manquant, que la logistique confirme — un reliquat n'ouvre rien. Une ligne
 * annoncée dont rien n'est arrivé part à zéro.
 */
export function FormulaireReception({
  identifiants,
  changerCommandes,
  initiales = {},
  numeroBlInitial = "",
  fichierBl,
  avant,
  libelleEnvoi = "Constater la réception",
  depuisUnBl = false,
  envoyer,
}: {
  identifiants: string[];
  changerCommandes: (ids: string[]) => void;
  initiales?: Record<string, Partial<SaisieLigne>>;
  numeroBlInitial?: string;
  fichierBl?: ReactNode;
  avant?: ReactNode;
  libelleEnvoi?: string;
  /** Proposée par un BL, une commande se retire même seule : on en choisit une autre. */
  depuisUnBl?: boolean;
  envoyer: (saisie: SaisieBl) => Promise<Constatee[]>;
}) {
  const naviguer = useNavigate();
  const cle = identifiants.join(",");
  const { donnees, erreur, enCours, recharger } = useChargement(
    async (signal) => {
      const [fiches, attendues] = await Promise.all([
        Promise.all(identifiants.map((id) => api.commandeFournisseur(id, signal))),
        api.commandesAReceptionner(signal),
      ]);
      return { fiches, attendues };
    },
    [cle],
  );
  const [saisies, setSaisies] = useState<Record<string, SaisieLigne>>({});
  const [numeroBl, setNumeroBl] = useState(numeroBlInitial);
  const [commentaires, setCommentaires] = useState<Record<string, string>>({});
  const [photos, setPhotos] = useState<Record<string, PhotoChoisie[]>>({});
  const [refus, setRefus] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  // La réception est écrite, mais une photo n'a pas suivi : on le dit, sans
  // offrir de reconstater ce qui l'est déjà.
  const [apresCoup, setApresCoup] = useState<string | null>(null);

  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees || enCours) return <Chargement message="Chargement de la réception…" />;

  const { fiches, attendues } = donnees;
  const fournisseur = fiches[0]?.fournisseur;
  const ajoutables = attendues.filter(
    (attendue) =>
      (!fournisseur || attendue.fournisseur.id === fournisseur.id) &&
      !identifiants.includes(attendue.id),
  );
  const lignesParId = new Map(
    fiches.flatMap((fiche) => fiche.lignes.map((ligne) => [ligne.id, ligne] as const)),
  );
  // Le formulaire se propose (décision 74) : ce que le BL annonce, sinon, sans BL, le
  // reste à recevoir. Ce n'est qu'un état de l'écran : rien n'est reçu avant la validation.
  const propose = (ligneId: string): SaisieLigne => {
    const ligne = lignesParId.get(ligneId);
    const resteARecevoir = !depuisUnBl && ligne && ligne.reliquat > 0 ? ligne.reliquat : null;
    return {
      ...VIDE,
      ...(resteARecevoir !== null ? { recue: String(resteARecevoir) } : {}),
      ...initiales[ligneId],
    };
  };
  const saisie = (ligneId: string): SaisieLigne => ({ ...propose(ligneId), ...saisies[ligneId] });
  // « Tout est arrivé conforme » valide le formulaire tel qu'il s'est proposé : une ligne
  // corrigée n'est plus telle qu'il s'est proposé, et le bouton s'écarte.
  const modifie = Object.keys(saisies).some(
    (ligneId) => JSON.stringify(saisie(ligneId)) !== JSON.stringify(propose(ligneId)),
  );
  const ecrire = (ligneId: string, champs: Partial<SaisieLigne>) =>
    setSaisies((avant) => {
      const apres = { ...saisie(ligneId), ...champs };
      // Plus rien ne manque : la confirmation, devenue sans objet, tombe.
      return {
        ...avant,
        [ligneId]: { ...apres, manquantConfirme: apres.manquantConfirme && !!manquant(apres) },
      };
    });
  const premiere = identifiants[0];

  if (apresCoup) {
    return (
      <div className="flex flex-col gap-4">
        <Erreur message={apresCoup} />
        <Link
          to={premiere ? `/commandes-fournisseurs/${premiere}` : "/receptions"}
          className="text-sm text-encre underline underline-offset-4"
        >
          {premiere ? "Voir la commande fournisseur" : "Les réceptions"}
        </Link>
      </div>
    );
  }

  const constater = async (telQuePropose = false) => {
    setRefus(null);
    if (fiches.length === 0) {
      setRefus("Choisissez la commande fournisseur que ce BL livre.");
      return;
    }
    const parts: SaisieBl["parts"] = [];
    for (const fiche of fiches) {
      const lueDe = (ligneId: string) => (telQuePropose ? propose(ligneId) : saisie(ligneId));
      const lignes = lignesAReceptionner(fiche)
        .filter((ligne) => nombre(lueDe(ligne.id).recue) > 0 || nombre(lueDe(ligne.id).annoncee) > 0)
        .map((ligne) => {
          const lue = lueDe(ligne.id);
          return {
            ligne_commande_fournisseur_id: ligne.id,
            quantite_annoncee: lue.annoncee.trim() ? nombre(lue.annoncee) : null,
            quantite_recue: nombre(lue.recue) || 0,
            quantite_non_conforme: nombre(lue.nonConforme) || 0,
            motif_non_conformite: (lue.motif || null) as MotifNonConformite | null,
            commentaire: lue.commentaire.trim() || null,
            manquant_confirme: lue.manquantConfirme,
          };
        });
      if (lignes.length === 0) {
        setRefus(
          `Rien n'est saisi pour la commande ${reference(fiche)} : saisissez ce qui est arrivé, ou retirez-la.`,
        );
        return;
      }
      parts.push({
        commande_fournisseur_id: fiche.id,
        lignes,
        commentaire: (commentaires[fiche.id] ?? "").trim() || null,
      });
    }
    // Une photo ne désigne qu'une ligne que l'on constate : une ligne cochée puis vidée
    // ferait montrer à la photo la réception entière, ce que personne n'a demandé.
    const constatees = new Set(
      parts.flatMap((part) => part.lignes.map((ligne) => ligne.ligne_commande_fournisseur_id)),
    );
    const orpheline = Object.values(photos)
      .flat()
      .find((photo) => photo.lignes.some((ligneId) => !constatees.has(ligneId)));
    if (orpheline) {
      setRefus(
        `La photo ${orpheline.fichier.name} montre une ligne dont rien n'est reçu : ` +
          "décochez-la, ou saisissez ce qui est arrivé.",
      );
      return;
    }
    setEnvoi(true);
    let receptions;
    try {
      receptions = await envoyer({ parts, numero_bl: numeroBl.trim() || null });
    } catch (cause) {
      setRefus(messageDe(cause));
      setEnvoi(false);
      return;
    }
    try {
      for (const reception of receptions) {
        const choisies = photos[reception.commande_fournisseur_id] ?? [];
        // Une photo désigne des lignes de la commande ; la réception constatée dit
        // laquelle de ses lignes est laquelle.
        const lignesDeLaReception = new Map<string, string>();
        if (choisies.some((photo) => photo.lignes.length > 0)) {
          const fiche = await api.commandeFournisseur(reception.commande_fournisseur_id);
          for (const ligne of fiche.receptions.find((c) => c.id === reception.id)?.lignes ?? []) {
            lignesDeLaReception.set(ligne.ligne_commande_fournisseur_id, ligne.id);
          }
        }
        for (const photo of choisies) {
          await api.joindrePhotoReception(
            reception.id,
            photo.fichier,
            photo.lignes.flatMap((ligneId) => lignesDeLaReception.get(ligneId) ?? []),
          );
        }
      }
    } catch (cause) {
      setApresCoup(
        `La réception est constatée, mais une photo n'a pas pu être jointe : ${messageDe(cause)}`,
      );
      return;
    }
    naviguer(`/commandes-fournisseurs/${premiere}`);
  };

  return (
    <div className="flex flex-col gap-5">
      <Link
        to="/receptions"
        className="inline-flex items-center gap-1.5 text-sm text-encre-douce hover:text-encre"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden />
        Les réceptions
      </Link>
      <div>
        <h1 className="break-words text-xl font-semibold text-encre">
          {fournisseur ? `Réception chez ${fournisseur.nom}` : "Réception d'un BL"}
        </h1>
        <p className="mt-1 text-sm text-encre-douce">
          {depuisUnBl
            ? "Le formulaire propose ce que le BL annonce."
            : "Le formulaire propose le reste à recevoir."}{" "}
          Corrigez ce qui diffère, même au-delà du commandé, et parmi ce qui est arrivé ce qui
          n'est pas conforme. Une ligne vide n'a rien reçu. Rien n'est reçu avant votre
          validation : quitter cet écran ne reçoit rien.
        </p>
      </div>

      {avant}

      <form
        aria-label="Constater une réception"
        className="flex flex-col gap-5"
        onSubmit={async (evenement) => {
          evenement.preventDefault();
          await constater();
        }}
      >
        <Carte>
          <EnTeteCarte>
            <h2 className="text-sm font-semibold text-encre">Le BL</h2>
          </EnTeteCarte>
          <CorpsCarte className="grid gap-3 sm:grid-cols-2">
            <Champ
              libelle="Numéro de BL"
              value={numeroBl}
              onChange={(evenement) => setNumeroBl(evenement.target.value)}
            />
            {fichierBl}
            {fiches.length > 1 ? (
              <p className="text-sm text-encre sm:col-span-2">
                BL commun : une réception sera créée pour chacune des {fiches.length} commandes,
                reliées à ce BL.
              </p>
            ) : null}
          </CorpsCarte>
        </Carte>

        {fiches.map((fiche) => (
          <PartDeLaReception
            key={fiche.id}
            fiche={fiche}
            depuisUnBl={depuisUnBl}
            saisie={saisie}
            ecrire={ecrire}
            commentaire={commentaires[fiche.id] ?? ""}
            onCommentaire={(texte) =>
              setCommentaires((avant) => ({ ...avant, [fiche.id]: texte }))
            }
            photos={photos[fiche.id] ?? []}
            onPhotos={(choisies) => setPhotos((avant) => ({ ...avant, [fiche.id]: choisies }))}
            onRetirer={
              fiches.length > 1 || depuisUnBl
                ? () => changerCommandes(identifiants.filter((id) => id !== fiche.id))
                : undefined
            }
          />
        ))}

        {ajoutables.length > 0 ? (
          <ChampSelection
            libelle={
              fournisseur
                ? "Le BL couvre aussi une autre commande de ce fournisseur"
                : "La commande fournisseur que ce BL livre"
            }
            aide={
              fournisseur
                ? "Un BL commun : chaque commande garde sa réception et son suivi."
                : undefined
            }
            value=""
            options={[
              { valeur: "", libelle: "Ajouter une commande" },
              ...ajoutables.map((attendue) => ({
                valeur: attendue.id,
                libelle: libelleAttendue(attendue, !fournisseur),
              })),
            ]}
            onChange={(evenement) => {
              if (evenement.target.value) {
                changerCommandes([...identifiants, evenement.target.value]);
              }
            }}
          />
        ) : null}

        {refus ? <Erreur message={refus} /> : null}

        <div className="flex flex-wrap items-center gap-2">
          <Bouton type="submit" variante="principal" disabled={envoi}>
            <PackageCheck className="h-4 w-4" aria-hidden />
            {libelleEnvoi}
          </Bouton>
          <Bouton
            type="button"
            variante="secondaire"
            disabled={envoi || modifie}
            onClick={() => constater(true)}
          >
            <PackageCheck className="h-4 w-4" aria-hidden />
            Tout est arrivé conforme
          </Bouton>
          {modifie ? (
            <p className="text-sm text-encre-douce">
              Vous avez corrigé une ligne : « Tout est arrivé conforme » ne valide que le
              formulaire tel qu'il s'est proposé.
            </p>
          ) : null}
          <Link
            to={premiere ? `/commandes-fournisseurs/${premiere}` : "/receptions"}
            className="inline-flex h-11 items-center px-4 text-sm text-encre-douce hover:text-encre"
          >
            Annuler
          </Link>
        </div>
      </form>
    </div>
  );
}

function libelleAttendue(attendue: CommandeAReceptionner, avecFournisseur: boolean) {
  return [
    ...(avecFournisseur ? [attendue.fournisseur.nom] : []),
    attendue.reference_fournisseur ?? "Sans référence",
    attendue.commandes_clients.map((c) => c.numero_commande_client).join(", "),
  ].join(" — ");
}

function reference(fiche: FicheCommandeFournisseur) {
  return (
    fiche.reference_fournisseur ??
    fiche.commandes_clients.map((servie) => servie.numero_commande_client).join(", ")
  );
}

function PartDeLaReception({
  fiche,
  depuisUnBl,
  saisie,
  ecrire,
  commentaire,
  onCommentaire,
  photos,
  onPhotos,
  onRetirer,
}: {
  fiche: FicheCommandeFournisseur;
  depuisUnBl: boolean;
  saisie: (ligneId: string) => SaisieLigne;
  ecrire: (ligneId: string, champs: Partial<SaisieLigne>) => void;
  commentaire: string;
  onCommentaire: (texte: string) => void;
  photos: PhotoChoisie[];
  onPhotos: (photos: PhotoChoisie[]) => void;
  onRetirer?: () => void;
}) {
  const lignes = lignesAReceptionner(fiche);
  return (
    <Carte>
      <EnTeteCarte className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="min-w-0 break-words text-sm font-semibold text-encre">
          Commande {reference(fiche)}
        </h2>
        {onRetirer ? (
          <Bouton type="button" taille="compact" variante="discret" onClick={onRetirer}>
            <X className="h-4 w-4" aria-hidden />
            Retirer cette commande
          </Bouton>
        ) : null}
      </EnTeteCarte>
      <CorpsCarte className="flex flex-col gap-4">
        {lignes.map((ligne) => (
          <LigneASaisir
            key={ligne.id}
            ligne={ligne}
            depuisUnBl={depuisUnBl}
            saisie={saisie(ligne.id)}
            ecrire={(champs) => ecrire(ligne.id, champs)}
          />
        ))}
        <ChampTexte
          libelle={`Commentaire de la réception (${reference(fiche)})`}
          value={commentaire}
          onChange={(evenement) => onCommentaire(evenement.target.value)}
        />
        <Champ
          libelle={`Photos de la réception (${reference(fiche)})`}
          type="file"
          accept="image/*"
          multiple
          className="py-2"
          aide={photos.length > 0 ? `${photos.length} photo(s) choisie(s)` : undefined}
          onChange={(evenement) =>
            onPhotos(
              Array.from(evenement.target.files ?? []).map((fichier) => ({ fichier, lignes: [] })),
            )
          }
        />
        {photos.map((photo, rang) => (
          <fieldset
            key={`${rang}-${photo.fichier.name}`}
            aria-label={`Ce que montre la photo ${photo.fichier.name}`}
            className="flex min-w-0 flex-col gap-1 rounded-[6px] border border-filet p-3"
          >
            <legend className="px-1 text-sm text-encre">
              La photo {photo.fichier.name} montre
            </legend>
            {lignes.map((ligne) => (
              <label
                key={ligne.id}
                className="flex min-h-11 cursor-pointer items-center gap-2 text-sm text-encre"
              >
                <input
                  type="checkbox"
                  checked={photo.lignes.includes(ligne.id)}
                  onChange={(evenement) =>
                    onPhotos(
                      photos.map((autre, i) =>
                        i === rang
                          ? {
                              ...autre,
                              lignes: evenement.target.checked
                                ? [...autre.lignes, ligne.id]
                                : autre.lignes.filter((id) => id !== ligne.id),
                            }
                          : autre,
                      ),
                    )
                  }
                  className="h-4 w-4 shrink-0 accent-laiton"
                />
                <span className="break-words">{ligne.designation}</span>
              </label>
            ))}
            <p className="text-xs text-encre-douce">
              {photo.lignes.length === 0
                ? "Aucune ligne cochée : la photo montre la réception entière."
                : `${photo.lignes.length} ligne(s) désignée(s).`}
            </p>
          </fieldset>
        ))}
      </CorpsCarte>
    </Carte>
  );
}

function LigneASaisir({
  ligne,
  depuisUnBl,
  saisie,
  ecrire,
}: {
  ligne: LigneDeLaFicheFournisseur;
  depuisUnBl: boolean;
  saisie: SaisieLigne;
  ecrire: (champs: Partial<SaisieLigne>) => void;
}) {
  const nonConforme = nombre(saisie.nonConforme) > 0;
  const manque = manquant(saisie);
  const reperes = [ligne.numero_commande_client, ligne.code_lot].filter(Boolean).join(" · ");
  return (
    <fieldset
      aria-label={ligne.designation}
      className="flex min-w-0 flex-col gap-3 rounded-[6px] border border-filet p-3"
    >
      <legend className="sr-only">{ligne.designation}</legend>
      <div className="min-w-0 text-sm">
        <p className="break-words font-medium text-encre">{ligne.designation}</p>
        <p className="text-encre-douce">
          {reperes} · commandé {quantiteLisible(ligne.quantite_commandee)} {ligne.unite}, reste{" "}
          {quantiteLisible(ligne.reliquat)} {ligne.unite} à recevoir
        </p>
      </div>
      <div className="grid gap-3 sm:grid-cols-3">
        {depuisUnBl ? (
          <Champ
            libelle="Annoncé par le BL"
            type="number"
            inputMode="decimal"
            min={0}
            step="any"
            value={saisie.annoncee}
            onChange={(evenement) => ecrire({ annoncee: evenement.target.value })}
          />
        ) : null}
        <Champ
          libelle="Reçu"
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          value={saisie.recue}
          onChange={(evenement) => ecrire({ recue: evenement.target.value })}
        />
        <Champ
          libelle="Dont non conforme"
          type="number"
          inputMode="decimal"
          min={0}
          step="any"
          value={saisie.nonConforme}
          onChange={(evenement) => ecrire({ nonConforme: evenement.target.value })}
        />
        {nonConforme ? (
          <ChampSelection
            libelle="Motif"
            required
            value={saisie.motif}
            options={OPTIONS_MOTIF}
            onChange={(evenement) => ecrire({ motif: evenement.target.value })}
          />
        ) : null}
      </div>
      {manque !== null ? (
        <label className="flex min-h-11 cursor-pointer items-center gap-2 text-sm text-encre">
          <input
            type="checkbox"
            checked={saisie.manquantConfirme}
            onChange={(evenement) => ecrire({ manquantConfirme: evenement.target.checked })}
            className="h-4 w-4 shrink-0 accent-laiton"
          />
          <span>
            Manquant : {quantiteLisible(manque)} {ligne.unite} annoncé(s) par le BL et absent(s).
            Le confirmer ouvre un problème fournisseur ; sinon, c'est un reliquat.
          </span>
        </label>
      ) : null}
      <Champ
        libelle="Commentaire"
        value={saisie.commentaire}
        onChange={(evenement) => ecrire({ commentaire: evenement.target.value })}
      />
    </fieldset>
  );
}
